// components/common/query/ReceitaFederalLookup.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Badge, Table, Alert, Spinner } from "flowbite-react";
import { HiOutlineEye, HiRefresh, HiExclamation } from "react-icons/hi";
import { maskCNPJ as maskCNPJBase, onlyCnpjChars } from "../../utils/masks";

/* ===== Utils locais (sem dependências externas) ===== */
const onlyDigits = (v = "") => String(v).replace(/\D+/g, "");
// CNPJ alfanumérico via util canônico (mantém fallback "—" para vazio).
const maskCNPJ = (cnpj = "") => maskCNPJBase(cnpj) || "—";

const formatDate = (iso) => {
  if (!iso || iso === "0000-00-00") return "—";
  // tenta YYYY-MM-DD, senão devolve como veio
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [_, y, mo, d] = m;
  try {
    const dt = new Date(`${y}-${mo}-${d}T00:00:00`);
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
    }).format(dt);
  } catch {
    return iso;
  }
};

const formatCEP = (cep = "") =>
  onlyDigits(cep).replace(/^(\d{5})(\d{3})$/, "$1-$2") || "—";

const formatMoneyBR = (v) => {
  if (v == null) return "—";
  // aceita "40000,00" ou number
  if (typeof v === "string") {
    const norm = v.replace(/\./g, "").replace(",", ".");
    const n = Number(norm);
    if (Number.isFinite(n)) {
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    }
  }
  if (typeof v === "number") {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
  return String(v);
};

const joinAddress = (obj = {}) => {
  const parts = [
    [obj.logradouro, obj.numero].filter(Boolean).join(", "),
    obj.complemento,
    obj.bairro,
    [obj.municipio, obj.uf].filter(Boolean).join(" - "),
    formatCEP(obj.cep),
  ].filter(Boolean);
  return parts.join(" • ");
};

const SectionCard = ({ title, children, className = "" }) => (
  <section
    className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}
  >
    {title && (
      <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
        {title}
      </div>
    )}
    {children}
  </section>
);

/* ===== Componente principal ===== */
export default function ModalReceitaFederal({
  cnpj,
  buttonLabel = "Abrir dados da Receita Federal",
  size = "7xl",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [data, setData] = useState(null);
  const controllerRef = useRef(null);

  // CNPJ alfanumérico: preserva letras (não usar onlyDigits aqui).
  const cnpjDigits = useMemo(() => onlyCnpjChars(cnpj), [cnpj]);

  const fetchData = async (force = false) => {
    if (!cnpjDigits || cnpjDigits.length !== 14) {
      setErr("CNPJ inválido ou ausente.");
      setData(null);
      setLoading(false);
      return;
    }
    if (data && !force) return; // já carregado
    setLoading(true);
    setErr(null);

    // cancela requisição anterior
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const ctl = new AbortController();
    controllerRef.current = ctl;

    try {
      const resp = await fetch(`https://api.opencnpj.org/${cnpjDigits}`, {
        method: "GET",
        signal: ctl.signal,
      });

      if (!resp.ok) {
        if (resp.status === 404) throw new Error("CNPJ não encontrado (404).");
        if (resp.status === 429)
          throw new Error(
            "Muitas requisições (429). Tente novamente em instantes."
          );
        throw new Error(`Erro ao consultar (${resp.status}).`);
      }

      const json = await resp.json();
      setData(json);
    } catch (e) {
      if (e.name !== "AbortError") {
        setErr(e.message || "Erro ao consultar os dados.");
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Abre o modal e busca (se ainda não buscou)
  const handleOpen = async () => {
    setOpen(true);
    setTimeout(() => fetchData(false), 0);
  };

  // Ao trocar o CNPJ, limpa dados (para evitar misturar respostas antigas)
  useEffect(() => {
    setData(null);
    setErr(null);
    setLoading(false);
    if (open) fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnpjDigits]);

  const payload = data || {}; // estrutura desconhecida -> protege acessos

  // Compatível com o JSON enviado no prompt do usuário
  const mapped = useMemo(() => {
    // tenta usar chaves do OpenCNPJ diretamente; se vier no seu formato, também mapeia
    const base = {
      cnpj: payload.cnpj || payload?.dadosCadastrais?.cnpj || "",
      razao_social:
        payload.razao_social || payload?.dadosCadastrais?.razaoSocial || "",
      nome_fantasia:
        payload.nome_fantasia || payload?.dadosCadastrais?.nomeFantasia || "",
      situacao_cadastral:
        payload.situacao_cadastral || payload?.dadosCadastrais?.situacao || "",
      data_situacao_cadastral:
        payload.data_situacao_cadastral ||
        payload?.dadosCadastrais?.dataSituacao ||
        "",
      natureza_juridica:
        payload.natureza_juridica ||
        payload?.dadosCadastrais?.naturezaJuridica ||
        "",
      data_inicio_atividade:
        payload.data_inicio_atividade ||
        payload?.dadosCadastrais?.dataFundacao ||
        "",
      porte_empresa:
        payload.porte_empresa || payload?.dadosCadastrais?.porte || "",
      capital_social:
        payload.capital_social || payload?.dadosCadastrais?.capitalSocial || "",
      cnae_principal:
        payload.cnae_principal ||
        payload?.dadosCadastrais?.cnaePrincipal ||
        payload?.dadosCadastrais?.ramoAtividade ||
        "",
      cnaes_secundarios:
        payload.cnaes_secundarios ||
        payload?.dadosCadastrais?.cnaesSecundarios ||
        [],
      logradouro:
        payload.logradouro || payload?.dadosCadastrais?.endereco || "",
      numero: payload.numero || payload?.dadosCadastrais?.numero || "",
      complemento:
        payload.complemento || payload?.dadosCadastrais?.complemento || "",
      bairro: payload.bairro || payload?.dadosCadastrais?.bairro || "",
      cep: payload.cep || payload?.dadosCadastrais?.cep || "",
      municipio: payload.municipio || payload?.dadosCadastrais?.cidade || "",
      uf: payload.uf || payload?.dadosCadastrais?.uf || "",
      email: payload.email || payload?.dadosCadastrais?.email || "",
      telefones: payload.telefones || payload?.dadosCadastrais?.telefones || [],
      QSA: payload.QSA || payload?.dadosCadastrais?.socios || [],
      opcao_simples:
        payload.opcao_simples || payload?.dadosCadastrais?.opcaoSimples || "",
      data_opcao_simples:
        payload.data_opcao_simples ||
        payload?.dadosCadastrais?.dataOpcaoSimples ||
        "",
      opcao_mei: payload.opcao_mei || payload?.dadosCadastrais?.opcaoMEI || "",
      data_opcao_mei:
        payload.data_opcao_mei || payload?.dadosCadastrais?.dataOpcaoMEI || "",
      matriz_filial:
        payload.matriz_filial || payload?.dadosCadastrais?.matrizFilial || "",
    };
    return base;
  }, [payload]);

  return (
    <div className={className}>
      <Button
        className="mt-1"
        color="light"
        size="xs"
        onClick={handleOpen}
        disabled={!cnpjDigits || cnpjDigits.length !== 14}
        title={
          !cnpjDigits || cnpjDigits.length !== 14
            ? "Informe um CNPJ válido (14 dígitos)"
            : ""
        }
      >
        <div className="flex items-center">
          <HiOutlineEye className="mr-1" />
          <span>{buttonLabel}</span>
        </div>
      </Button>

      <Modal show={open} size={size} onClose={() => setOpen(false)}>
        <Modal.Header className="py-2">
          <div className="flex items-center gap-2 text-base mt-1">
            <span>Dados da Receita Federal</span>
            <Badge color="gray" className="w-fit">
              {maskCNPJ(cnpjDigits)}
            </Badge>
          </div>
        </Modal.Header>
        <Modal.Body className="bg-slate-50">
          {/* Erro */}
          {err && (
            <Alert color="failure" className="mb-4">
              <div className="flex items-center gap-2">
                <HiExclamation className="text-lg" />
                <span>{err}</span>
              </div>
            </Alert>
          )}

          {/* Loading inicial */}
          {!err && loading && !data && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
              Carregando informações do CNPJ…
            </div>
          )}

          {/* Conteúdo */}
          {!loading && !err && data && (
            <div className="space-y-5">
              {/* Visão geral */}
              <SectionCard title="Visão geral">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Razão Social
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {mapped.razao_social || "—"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Nome Fantasia
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {mapped.nome_fantasia || "—"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      CNPJ
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {maskCNPJ(mapped.cnpj)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Situação Cadastral
                    </div>
                    <div className="mt-1">
                      <Badge
                        color={
                          (mapped.situacao_cadastral || "")
                            .toLowerCase()
                            .includes("ativa")
                            ? "success"
                            : "failure"
                        }
                        className="w-fit"
                      >
                        {mapped.situacao_cadastral || "—"}
                      </Badge>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Natureza Jurídica
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {mapped.natureza_juridica || "—"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Abertura
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {formatDate(mapped.data_inicio_atividade)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Porte
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {mapped.porte_empresa || "—"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Capital Social
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {formatMoneyBR(mapped.capital_social)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Matriz/Filial
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">
                      {mapped.matriz_filial || "—"}
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* Endereço & Contatos */}
              <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard title="Endereço">
                  <div className="text-sm text-slate-700">
                    {joinAddress(mapped)}
                  </div>
                </SectionCard>
                <SectionCard title="Contato">
                  <div className="space-y-2 text-sm text-slate-700">
                    <div>
                      <span className="text-slate-500">E-mail:</span>{" "}
                      {mapped.email || "—"}
                    </div>
                    <div>
                      <span className="text-slate-500">Telefones:</span>{" "}
                      {Array.isArray(mapped.telefones) &&
                      mapped.telefones.length > 0 ? (
                        <span className="inline-flex flex-wrap gap-2">
                          {mapped.telefones.map((t, i) => (
                            <Badge key={i} color="gray" className="w-fit">
                              {(t?.ddd ? `(${t.ddd}) ` : "") +
                                (t?.numero || "—")}
                              {t?.is_fax ? " • fax" : ""}
                            </Badge>
                          ))}
                        </span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </SectionCard>
              </section>

              {/* CNAEs */}
              <SectionCard title="Atividade Econômica (CNAE)">
                <div className="text-sm text-slate-700">
                  <div className="mb-2">
                    <span className="text-slate-500">Principal:</span>{" "}
                    <Badge color="info" className="w-fit">
                      {mapped.cnae_principal || "—"}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-slate-500">Secundários:</span>{" "}
                    {Array.isArray(mapped.cnaes_secundarios) &&
                    mapped.cnaes_secundarios.length > 0 ? (
                      <span className="mt-1 inline-flex flex-wrap gap-2">
                        {mapped.cnaes_secundarios.map((c, i) => (
                          <Badge key={i} color="gray" className="w-fit">
                            {c}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </SectionCard>

              {/* Simples/MEI */}
              <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Optante Simples
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {mapped.opcao_simples === "S"
                      ? "Sim"
                      : mapped.opcao_simples === "N"
                      ? "Não"
                      : "—"}
                    {mapped.data_opcao_simples && (
                      <span className="ml-2 text-[11px] text-slate-500">
                        {formatDate(mapped.data_opcao_simples)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Optante MEI
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {mapped.opcao_mei === "S"
                      ? "Sim"
                      : mapped.opcao_mei === "N"
                      ? "Não"
                      : "—"}
                    {mapped.data_opcao_mei && (
                      <span className="ml-2 text-[11px] text-slate-500">
                        {formatDate(mapped.data_opcao_mei)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Situação
                  </div>
                  <div className="mt-1">
                    <Badge
                      color={
                        (mapped.situacao_cadastral || "")
                          .toLowerCase()
                          .includes("ativa")
                          ? "success"
                          : "failure"
                      }
                      className="w-fit"
                    >
                      {mapped.situacao_cadastral || "—"}
                    </Badge>
                  </div>
                </div>
              </section>

              {/* QSA */}
              <SectionCard title="Quadro Societário (QSA)">
                {Array.isArray(mapped.QSA) && mapped.QSA.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <Table>
                      <Table.Head className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        <Table.HeadCell className="py-2">Nome</Table.HeadCell>
                        <Table.HeadCell className="py-2">
                          Qualificação
                        </Table.HeadCell>
                        <Table.HeadCell className="py-2">
                          Identificador
                        </Table.HeadCell>
                        <Table.HeadCell className="py-2">
                          Entrada
                        </Table.HeadCell>
                        <Table.HeadCell className="py-2">
                          Faixa Etária
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {mapped.QSA.map((s, i) => (
                          <Table.Row key={i}>
                            <Table.Cell className="font-medium text-slate-900">
                              {s?.nome_socio || s?.nome || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-slate-700">
                              {s?.qualificacao_socio || s?.qualificacao || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-slate-700">
                              {s?.identificador_socio || s?.tipo || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-slate-700">
                              {formatDate(
                                s?.data_entrada_sociedade || s?.entrada || ""
                              )}
                            </Table.Cell>
                            <Table.Cell className="text-slate-700">
                              {s?.faixa_etaria || "—"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <div className="text-sm text-slate-600">
                    Sem registros de QSA.
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* Nada para mostrar (ex.: CNPJ inválido) */}
          {!loading && !err && !data && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Informe um CNPJ válido para consultar.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
